import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import { APP } from "@/config";
import { LogOut, KeyRound, ShieldAlert, TrendingUp, Users2, MailCheck } from "lucide-react";
import AdminUsers from "@/pages/AdminUsers";
import AdminTeachers from "@/pages/AdminTeachers";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [skills, setSkills] = useState([]);
  const [legacyExists, setLegacyExists] = useState(false);

  useEffect(() => {
    if (user.role !== "admin" || user.impersonated_by) { nav("/"); return; }
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => setOverview({}));
    api.get("/skills").then(({ data }) => setSkills(data));
    api.get("/admin/legacy-admin").then(({ data }) => setLegacyExists(data.exists)).catch(() => {});
  }, []);

  if (!overview) return <div className="band-6-12 min-h-screen bg-[var(--ns-bg)]"><Spinner /></div>;

  const byGrade = {};
  skills.forEach((s) => { (byGrade[s.grade] = byGrade[s.grade] || []).push(s); });

  return (
    <div className="band-6-12 min-h-screen bg-[var(--ns-bg)]">
      <header className="border-b border-[var(--ns-border)] bg-[var(--ns-surface)]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <span className="font-head font-bold text-[var(--ns-text)]">{APP.name} · Admin</span>
          <Button variant="ghost" data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}><LogOut size={16} /> Log out</Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-5 py-8">
        <h1 className="font-head text-3xl font-bold mb-6">Platform overview</h1>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10">
          {[["Users", overview.users], ["Students", overview.students], ["Skills", overview.skills], ["Tutor sessions", overview.tutor_sessions], ["Diagnostics", overview.diagnostics_completed]].map(([l, v]) => (
            <Card key={l} className="p-4"><div className="text-xs uppercase tracking-wide text-[var(--ns-subtle)] font-semibold">{l}</div><div className="font-head text-3xl font-bold mt-1">{v ?? 0}</div></Card>
          ))}
        </div>

        <GrowthFunnel />

        <h2 className="font-head text-2xl font-bold mb-4">Account &amp; maintenance</h2>
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          <ChangePasswordCard />
          <LegacyAdminCard legacyExists={legacyExists} onRemoved={() => setLegacyExists(false)} />
        </div>

        <div className="mb-10"><AdminUsers /></div>

        <div className="mb-10"><AdminTeachers /></div>

        <h2 className="font-head text-2xl font-bold mb-4">Skill graph (grades 3–8)</h2>
        <div className="grid gap-5">
          {Object.keys(byGrade).sort().map((g) => (
            <div key={g}>
              <div className="text-sm font-semibold text-[var(--ns-primary)] mb-2">Grade {g}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {byGrade[g].map((s) => (
                  <Card key={s.id} className="p-4" data-testid={`admin-skill-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[var(--ns-text)]">{s.name}</span>
                      <Pill tone="muted">{s.strand}</Pill>
                    </div>
                    <p className="text-sm text-[var(--ns-muted)] mt-1">{s.short_description}</p>
                    {s.prerequisites.length > 0 && (
                      <div className="mt-2 text-xs text-[var(--ns-subtle)]">Requires: {s.prerequisites.join(", ")}</div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const inputCls = "mt-2 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-3 focus:outline-none focus:border-[var(--ns-primary)]";

function GrowthFunnel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/funnel?days=${days}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [days]);

  const maxCount = data ? Math.max(1, ...data.steps.map((s) => s.count)) : 1;

  return (
    <div className="mb-10" data-testid="growth-funnel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-head text-2xl font-bold flex items-center gap-2"><TrendingUp size={22} className="text-[var(--ns-primary)]" /> Growth funnel</h2>
        <div className="flex rounded-full bg-[var(--ns-surface-subtle)] p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`funnel-days-${d}`} onClick={() => setDays(d)}
              className="rounded-full px-4 py-1.5 text-sm font-semibold transition-colors"
              style={{ background: days === d ? "var(--ns-surface)" : "transparent", color: days === d ? "var(--ns-primary)" : "var(--ns-muted)" }}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {!data ? <Spinner label="Loading funnel" /> : (
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-5">
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wide text-[var(--ns-subtle)] font-semibold mb-3">Diagnostic funnel · {days} days</div>
            <div className="space-y-3">
              {data.steps.map((s) => (
                <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold text-[var(--ns-text)]">{s.label}</span>
                    <span className="text-[var(--ns-muted)]">{s.count} <span className="text-[var(--ns-subtle)]">· {s.rate}%</span></span>
                  </div>
                  <div className="h-3 rounded-full bg-[var(--ns-surface-subtle)] overflow-hidden">
                    <div className="h-full bg-[var(--ns-primary)] rounded-full transition-all duration-500" style={{ width: `${Math.round((100 * s.count) / maxCount)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[var(--ns-border)] text-sm">
              Overall start→signup: <span className="font-head font-bold text-[var(--ns-primary)]">{data.overall_conversion}%</span>
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wide text-[var(--ns-subtle)] font-semibold mb-3 flex items-center gap-1"><Users2 size={14} /> Email leads · {days} days</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <LeadStat label="Captured" value={data.leads.captured} />
              <LeadStat label="Confirmed" value={data.leads.confirmed} />
              <LeadStat label="Unsub" value={data.leads.unsubscribed} />
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--ns-muted)]">
              <MailCheck size={16} className="text-[var(--ns-primary)]" /> Confirm rate: <span className="font-semibold text-[var(--ns-text)]">{data.leads.confirm_rate}%</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

const LeadStat = ({ label, value }) => (
  <div className="rounded-lg bg-[var(--ns-surface-subtle)] py-3">
    <div className="font-head text-2xl font-bold text-[var(--ns-text)]">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-[var(--ns-subtle)] font-semibold">{label}</div>
  </div>
);

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated. Use it next time you log in.");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not update password");
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-5" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1"><KeyRound size={18} className="text-[var(--ns-primary)]" /><span className="font-head font-bold text-[var(--ns-text)]">Change password</span></div>
      <p className="text-sm text-[var(--ns-muted)] mb-4">Rotate your admin password instantly — no redeploy needed.</p>
      <form onSubmit={submit} className="grid gap-3">
        <div>
          <label className="text-sm font-semibold text-[var(--ns-text)]">Current password</label>
          <input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-semibold text-[var(--ns-text)]">New password</label>
          <input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-semibold text-[var(--ns-text)]">Confirm new password</label>
          <input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} />
        </div>
        <Button type="submit" data-testid="cp-submit" disabled={loading} className="mt-1">{loading ? "Updating…" : "Update password"}</Button>
      </form>
    </Card>
  );
}

function LegacyAdminCard({ legacyExists, onRemoved }) {
  const [loading, setLoading] = useState(false);

  const remove = async () => {
    setLoading(true);
    try {
      const { data } = await api.delete("/admin/legacy-admin");
      if (data.removed) { toast.success("Old admin account removed."); onRemoved(); }
      else { toast.info(data.detail || "No legacy admin to remove."); onRemoved(); }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove legacy admin");
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-5" data-testid="legacy-admin-card">
      <div className="flex items-center gap-2 mb-1"><ShieldAlert size={18} className="text-[var(--ns-primary)]" /><span className="font-head font-bold text-[var(--ns-text)]">Legacy admin cleanup</span></div>
      {legacyExists ? (
        <>
          <p className="text-sm text-[var(--ns-muted)] mb-4">An old default admin account (<span className="font-mono">admin@numbersense.app</span>) still exists. Remove it so you are the only admin.</p>
          <Button variant="outline" data-testid="remove-legacy-btn" onClick={remove} disabled={loading} className="!border-[#E05A47] !text-[#E05A47] hover:!bg-[#FDECEA]">{loading ? "Removing…" : "Remove old admin account"}</Button>
        </>
      ) : (
        <p className="text-sm text-[var(--ns-muted)]" data-testid="legacy-admin-clean">No legacy admin account found — you are the only admin.</p>
      )}
    </Card>
  );
}
